import { useEffect, useRef, useState } from 'react';
import type { RenderAsset } from '@sarnia/shared';

type AssetMap = Record<string, RenderAsset>;

// The preview iframe is sandboxed (opaque origin) so it cannot send our login cookie; images are therefore
// fetched here (with the cookie) and handed to the preview as data: URLs.
const dataUrlCache = new Map<string, string | null>();
const inflight = new Map<string, Promise<string | null>>();

async function fetchDataUrl(asset: RenderAsset): Promise<string | null> {
  const key = `${asset.assetId}|${asset.url}`;
  const cached = dataUrlCache.get(key);
  if (cached !== undefined) return cached;
  const pending = inflight.get(key);
  if (pending) return pending;

  const promise = (async () => {
    try {
      const response = await fetch(asset.url, { credentials: 'same-origin' });
      if (!response.ok) throw new Error('Failed to fetch asset');
      const blob = await response.blob();
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(String(reader.result));
        reader.onerror = () => reject(new Error('Failed to read asset'));
        reader.readAsDataURL(blob);
      });
      dataUrlCache.set(key, dataUrl);
      return dataUrl;
    } catch {
      dataUrlCache.set(key, null);
      return null;
    } finally {
      inflight.delete(key);
    }
  })();

  inflight.set(key, promise);
  return promise;
}

interface Resolved {
  key: string;
  urls: Record<string, string>;
}

/**
 * Resolve data: URLs for the wanted assets. `ready` is true once every wanted asset has been tried (a failed fetch counts
 * as resolved, it just has no url). The effect only re-runs when the set of wanted ids or their urls change, and it
 * never reads state that it also writes, so it cannot loop.
 */
export function useAssetDataUrls(assets: AssetMap, wantedIds: string[]): { urls: Record<string, string>; ready: boolean } {
  const assetsRef = useRef(assets);
  assetsRef.current = assets;

  const key = [...wantedIds]
    .sort()
    .map((id) => `${id}:${assets[id]?.url ?? ''}`)
    .join('|');
  const [resolved, setResolved] = useState<Resolved>({ key: '', urls: {} });

  useEffect(() => {
    if (key === '') return undefined;
    let cancelled = false;
    const ids = wantedIds;
    void Promise.all(
      ids.map(async (id) => {
        const asset = assetsRef.current[id];
        return { id, url: asset ? await fetchDataUrl(asset) : null };
      })
    ).then((results) => {
      if (cancelled) return;
      const urls: Record<string, string> = {};
      for (const r of results) if (r.url) urls[r.id] = r.url;
      setResolved({ key, urls });
    });
    return () => {
      cancelled = true;
    };
    // `key` fully describes the inputs (ids + urls); wantedIds is read once per key change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const ready = key === '' || resolved.key === key;
  return { urls: resolved.urls, ready };
}
